import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export const useFavorites = () => {
  const { user } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);

  // Fetch user's favorites
  useEffect(() => {
    if (!user) {
      setFavoriteIds(new Set());
      return;
    }

    const fetchFavorites = async () => {
      const { data } = await supabase
        .from('saved_properties')
        .select('property_id')
        .eq('user_id', user.id);

      if (data) {
        setFavoriteIds(new Set(data.map(d => d.property_id)));
      }
    };

    fetchFavorites();
  }, [user]);

  const toggleFavorite = useCallback(async (propertyId: number): Promise<boolean> => {
    if (!user) return false;

    setLoading(true);
    try {
      if (favoriteIds.has(propertyId)) {
        await supabase
          .from('saved_properties')
          .delete()
          .eq('user_id', user.id)
          .eq('property_id', propertyId);

        setFavoriteIds(prev => {
          const next = new Set(prev);
          next.delete(propertyId);
          return next;
        });
      } else {
        await supabase
          .from('saved_properties')
          .insert({ user_id: user.id, property_id: propertyId });

        setFavoriteIds(prev => new Set(prev).add(propertyId));
      }
      return true;
    } finally {
      setLoading(false);
    }
  }, [user, favoriteIds]);

  const isFavorite = useCallback((propertyId: number) => {
    return favoriteIds.has(propertyId);
  }, [favoriteIds]);

  return { isFavorite, toggleFavorite, loading };
};
